import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import type { HotelDetailV2Model } from "../model/hotelDetailV2.model";
import { EmptyContent } from "./AboutSection";

interface ReviewsSectionProps {
  hotel: HotelDetailV2Model;
}

export default function ReviewsSection({ hotel }: ReviewsSectionProps) {
  const rating = hotel.rating;
  const categories = hotel.ratingCategories;
  const reviews = hotel.reviews;

  return (
    <section
      id="reviews"
      className="scroll-mt-28 rounded-2xl border border-[rgb(229,231,235)] bg-white p-5"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[#1a1a2e]">نظرات کاربران</h2>
        {rating?.isSample ? <SampleBadge /> : null}
      </div>

      {reviews.length === 0 ? (
        <EmptyContent text="هنوز نظری برای این هتل ثبت نشده است." />
      ) : rating ? (
        <div className={`mb-6 grid gap-6 rounded-2xl bg-[#f5f6f8] p-5 ${
          categories.length > 0 ? "md:grid-cols-3" : ""
        }`}>
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-1 text-5xl font-black text-emerald-600">
              {Number((rating.value / 2).toFixed(1)).toLocaleString("fa-IR")}
            </div>
            <div className="mb-0.5 text-base font-bold text-[#1a1a2e]">
              {rating.label}
            </div>
            <div className="text-xs text-[#6b7280]">
              پیشنهاد {rating.count.toLocaleString("fa-IR")} مسافر
            </div>
          </div>
          {categories.length > 0 ? (
            <div className="grid gap-x-8 gap-y-3 md:col-span-2 md:grid-cols-2">
              {categories.map((item) => (
                <div key={item.title} className="flex items-center gap-2">
                  <span className="w-20 shrink-0 text-right text-xs text-[#6b7280]">
                    {item.title}
                  </span>
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#e5e7eb]">
                    <span
                      className="block h-full rounded-full bg-emerald-600"
                      style={{ width: `${Math.min(item.value * 10, 100)}%` }}
                    />
                  </span>
                  <span className="w-7 shrink-0 text-xs font-bold text-[#1a1a2e]">
                    {Number(item.value.toFixed(1)).toLocaleString("fa-IR")}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {reviews.length > 0 ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            {reviews.slice(0, 3).map((review) => (
              <article key={review.id} className="rounded-2xl border border-[rgb(229,231,235)] p-4">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                      {review.author.slice(0, 1)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#1a1a2e]">
                        {review.author}
                      </p>
                      <p className="text-xs text-[#6b7280]">{review.dateLabel}</p>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-lg bg-emerald-600 px-2 py-1 text-xs font-bold text-white">
                    {Number((review.value / 2).toFixed(1)).toLocaleString("fa-IR")}
                  </span>
                </div>
                {review.verified ? (
                  <span className="mb-2 inline-block rounded-full bg-[#e6f7f1] px-2 py-1 text-xs font-medium text-emerald-700">
                    تایید شده
                  </span>
                ) : null}
                <p className="mt-1 text-sm leading-6 text-[#1a1a2e]">{review.body}</p>
                <button type="button" className="mt-3 flex items-center gap-1.5 text-xs text-[#6b7280]">
                  <PhosphorIcon name="ThumbsUp" size={11} />
                  مفید بود
                </button>
              </article>
            ))}
          </div>
          {reviews.length > 3 ? (
            <button
              type="button"
              className="mt-4 w-full rounded-xl border border-[rgb(229,231,235)] py-3 text-sm font-bold text-primary"
            >
              مشاهده همه نظرات
            </button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export function SampleBadge() {
  return (
    <span className="shrink-0 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-bold text-amber-700">
      نمونه تا اتصال API
    </span>
  );
}
